import { NewsStory } from "../types/NewsTypes";
import { headlineNewsItemsCount } from "../constants";

/**
 * Moves up the two most recent headline stories to the top of the list.
 * Preserves order for all non-headline stories.
 */
export const sortStoriesWithHeadlines = (stories: unknown): NewsStory[] => {
  // Return an empty array if the input is not an array
  if (!Array.isArray(stories)) return [];

  // Separate stories into headline and non-headline arrays in a single pass
  const { headlines, nonHeadlines } = (stories as NewsStory[]).reduce(
    // Check if the story is a headline and categorize accordingly
    (categorizedStories, story) => {
      if (story.content?.isHeadline) {
        return {
          headlines: [...categorizedStories.headlines, story],
          nonHeadlines: categorizedStories.nonHeadlines,
        };
      } else {
        return {
          headlines: categorizedStories.headlines,
          nonHeadlines: [...categorizedStories.nonHeadlines, story],
        };
      }
    },
    {
      headlines: [] as NewsStory[],
      nonHeadlines: [] as NewsStory[],
    }
  );

  // Create a copy of headlines and sort them by published date in descending order without mutating the original array
  const sortedByDate = [...headlines].sort(
    (a, b) =>
      new Date(b.published_at ?? "").getTime() -
      new Date(a.published_at ?? "").getTime()
  );

  // Limit the number of headline stories to the configured count
  const sortedHeadlines = sortedByDate.slice(0, headlineNewsItemsCount);

  // Create a set of UUIDs for the selected headline stories
  const selectedUUIDs = new Set(sortedHeadlines.map((s) => s.uuid));

  // Filter out any non-headline stories that are already in the headline list
  const remainingStories = nonHeadlines.filter(
    (s) => !selectedUUIDs.has(s.uuid)
  );

  // Combine the sorted headlines with the remaining non-headline stories
  return [...sortedHeadlines, ...remainingStories];
};
